<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import LiveChatFormsPanel from './contact/LiveChatFormsPanel.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);
const showLiveChatFormsPanel = ref(false);
const { t } = useI18n();

const currentAccountId = useMapGetter('getCurrentAccountId');

const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearIntegrationEnabled = computed(
  () => linearIntegration.value?.enabled || false
);

const isLinearFeatureEnabled = isFeatureEnabledonAccount.value(
  currentAccountId.value,
  FEATURE_FLAGS.LINEAR
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const openLiveChatFormsPanel = () => {
  showLiveChatFormsPanel.value = true;
};

const closeLiveChatFormsPanel = () => {
  showLiveChatFormsPanel.value = false;
};

const headerTitle = computed(() => {
  return showLiveChatFormsPanel.value
    ? t('CONTACT_PANEL.LIVE_CHAT_FORMS.TITLE')
    : t('CONVERSATION.SIDEBAR.CONTACT');
});

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
  // Load issues for issue assignment
  store.dispatch('issues/get');
});

watch(
  () => currentChat.value?.id,
  () => {
    showLiveChatFormsPanel.value = false;
  }
);
</script>

<template>
  <div class="w-full h-full wlab-profile-shell flex flex-col">
    <div class="wlab-profile-shell__head">
      <button
        v-if="showLiveChatFormsPanel"
        type="button"
        class="wlab-profile-shell__back"
        @click="closeLiveChatFormsPanel"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          width="16"
          height="16"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m15 18-6-6 6-6"
          />
        </svg>
        Back
      </button>
      <div v-else style="width: 36px; height: 36px" />
      <div class="wlab-profile-shell__title">{{ headerTitle }}</div>
      <button
        type="button"
        class="wlab-profile-shell__close"
        aria-label="Close"
        @click="closeContactPanel"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          width="20"
          height="20"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m6 6 12 12m0-12L6 18"
          />
        </svg>
      </button>
    </div>
    <template v-if="showLiveChatFormsPanel">
      <div class="wlab-profile-shell__body flex-1 min-h-0 overflow-y-auto">
        <LiveChatFormsPanel
          :conversation="currentChat"
          :contact-attributes="contactAdditionalAttributes"
          @close="closeLiveChatFormsPanel"
        />
      </div>
    </template>
    <template v-else>
      <div class="wlab-profile-shell__body flex-1 min-h-0 overflow-y-auto">
        <ContactInfo
          :contact="contact"
          :channel-type="channelType"
          :conversation-id="conversationId"
          @open-live-chat-forms="openLiveChatFormsPanel"
        />

        <div class="wlab-conv-action-card">
          <ConversationAction
            :conversation-id="conversationId"
            :inbox-id="inboxId"
          />
        </div>

        <div class="list-group wlab-list-group">
          <Draggable
            :list="conversationSidebarItems"
            animation="200"
            ghost-class="ghost"
            handle=".drag-handle"
            item-key="name"
            class="flex flex-col"
            @start="dragging = true"
            @end="onDragEnd"
          >
            <template #item="{ element }">
              <div
                v-if="element.name === 'conversation_participants'"
                class="conversation--actions"
              >
                <AccordionItem
                  :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
                  :is-open="
                    isContactSidebarItemOpen('is_conv_participants_open')
                  "
                  @toggle="
                    value =>
                      toggleSidebarUIState('is_conv_participants_open', value)
                  "
                >
                  <ConversationParticipant
                    :conversation-id="conversationId"
                    :inbox-id="inboxId"
                  />
                </AccordionItem>
              </div>
              <div v-else-if="element.name === 'conversation_info'">
                <AccordionItem
                  :title="
                    $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')
                  "
                  :is-open="isContactSidebarItemOpen('is_conv_details_open')"
                  compact
                  @toggle="
                    value => toggleSidebarUIState('is_conv_details_open', value)
                  "
                >
                  <ConversationInfo
                    :conversation-attributes="conversationAdditionalAttributes"
                    :contact-attributes="contactAdditionalAttributes"
                  />
                </AccordionItem>
              </div>
              <div
                v-else-if="element.name === 'contact_attributes'"
                class="pt-4"
              >
                <AccordionItem
                  :title="
                    $t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')
                  "
                  :is-open="
                    isContactSidebarItemOpen('is_contact_attributes_open')
                  "
                  compact
                  @toggle="
                    value =>
                      toggleSidebarUIState('is_contact_attributes_open', value)
                  "
                >
                  <CustomAttributes
                    attribute-type="contact_attribute"
                    attribute-from="conversation_contact_panel"
                    :contact-id="contact.id"
                    :empty-state-message="
                      $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                    "
                  />
                </AccordionItem>
              </div>
              <div v-else-if="element.name === 'previous_conversation'">
                <AccordionItem
                  v-if="contact.id"
                  :title="
                    $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
                  "
                  :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
                  compact
                  @toggle="
                    value =>
                      toggleSidebarUIState('is_previous_conv_open', value)
                  "
                >
                  <ContactConversations
                    :contact-id="contact.id"
                    :conversation-id="conversationId"
                  />
                </AccordionItem>
              </div>
              <woot-feature-toggle
                v-else-if="element.name === 'macros'"
                feature-key="macros"
              >
                <AccordionItem
                  :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
                  :is-open="isContactSidebarItemOpen('is_macro_open')"
                  compact
                  @toggle="
                    value => toggleSidebarUIState('is_macro_open', value)
                  "
                >
                  <MacrosList :conversation-id="conversationId" />
                </AccordionItem>
              </woot-feature-toggle>
              <div
                v-else-if="
                  element.name === 'linear_issues' && isLinearFeatureEnabled
                "
              >
                <AccordionItem
                  :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
                  :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
                  compact
                  @toggle="
                    value =>
                      toggleSidebarUIState('is_linear_issues_open', value)
                  "
                >
                  <LinearSetupCTA v-if="!isLinearIntegrationEnabled" />
                  <LinearIssuesList v-else :conversation-id="conversationId" />
                </AccordionItem>
              </div>
              <div
                v-else-if="
                  element.name === 'shopify_orders' && isShopifyFeatureEnabled
                "
              >
                <AccordionItem
                  :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
                  :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
                  compact
                  @toggle="
                    value =>
                      toggleSidebarUIState('is_shopify_orders_open', value)
                  "
                >
                  <ShopifyOrdersList :contact-id="contactId" />
                </AccordionItem>
              </div>
              <div v-else-if="element.name === 'contact_notes'">
                <AccordionItem
                  :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
                  :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
                  compact
                  @toggle="
                    value =>
                      toggleSidebarUIState('is_contact_notes_open', value)
                  "
                >
                  <ContactNotes :contact-id="contactId" />
                </AccordionItem>
              </div>
            </template>
          </Draggable>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss">
.ghost {
  opacity: 0.6;
}
</style>
